import DetailCardContainer from "@ui-kit/data-display/DetailCardContainer/DetailCardContainer.web";

import { VenueBasedMembershipGroupSerializer } from "@app-contexts/dashboard/features/memberships/data/types/membership-groups";
import { NestedPublicVenueSerializer } from "@app-contexts/public/features/event-detail/data/types/events";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { enumsTypes } from "@shared/constants";
import MembershipTypesContainer, {
  MembershipTypesContainerType,
} from "@shared/modules/memberships/components/MembershipTypesContainer/MembershipTypesContainer";
import {
  PublicMembershipGroupSerializer,
  PublicMembershipLevelSerializer,
} from "@shared/modules/memberships/types";
import { useTranslation } from "@shared/utils/internationalization";
import ItemQuantitySelectInfo from "@ui-application/data-display/ItemQuantitySelectInfo/ItemQuantitySelectInfo.web";

export default function MembershipTypesList({
  membershipGroup,
  membershipLevels,
  venue,
  currency,
  hasAllInPricing,
  isLoading,
  onAmountRequested,
  forceQuantityButtons = false,
  withDebounce = true,
  initialSelectedMembershipLevels,
  enableTracking = true,
}: {
  membershipGroup:
    | PublicMembershipGroupSerializer
    | VenueBasedMembershipGroupSerializer;
  membershipLevels: Array<
    | PublicMembershipLevelSerializer
    | VenueBasedMembershipGroupSerializer["membership_levels"][0]
  >;
  venue: NestedPublicVenueSerializer;
  currency: enumsTypes.CurrencyMapIdType;
  hasAllInPricing: boolean;
  isLoading: boolean;
  onAmountRequested?: MembershipTypesContainerType["onAmountRequested"];
  forceQuantityButtons?: boolean;
  withDebounce?: boolean;
  /**
   * Used to display an initial amount requested to pass to MembershipTypesContainer
   *
   * keys are membership level ids and values are the amount requested
   */
  initialSelectedMembershipLevels?: Record<string, number>;
  enableTracking?: boolean;
}) {
  const { t } = useTranslation("purchases");

  // No results
  if (!isLoading && !membershipLevels.length) {
    return (
      <Flex width="100%" justifyContent="flex-start">
        <Text as="h2" fontSize={{ base: "md", sm: "3xl" }} fontWeight="bold">
          {t("No membership levels found.")}
        </Text>
      </Flex>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <DetailCardContainer>
        <ItemQuantitySelectInfo
          currency={currency}
          isLoading={isLoading}
          amountSelected={0}
        />
      </DetailCardContainer>
    );
  }

  return (
    <Flex w="full" direction="column" gap="md" style={{ marginTop: 0 }}>
      {membershipLevels.map((level) => {
        return (
          <MembershipTypesContainer
            key={level.id}
            venue={venue}
            selectedAttractionMembership={membershipGroup}
            isLoading={isLoading}
            membershipLevel={level}
            hasAllInPricing={hasAllInPricing}
            onAmountRequested={onAmountRequested}
            forceQuantityButtons={forceQuantityButtons}
            withDebounce={withDebounce}
            initialAmountRequested={initialSelectedMembershipLevels?.[level.id]}
            enableTracking={enableTracking}
          />
        );
      })}
    </Flex>
  );
}
